import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Calendar, Paperclip } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, saasApi } from '../../services/api';
import { createPurchaseRouted } from '@/offline';
import toast from 'react-hot-toast';
import SupplierSelect from '../common/SupplierSelect';
import ItemServiceSelect from '../common/ItemServiceSelect';
import BankAccountSelect from '../common/BankAccountSelect';
import ImeiSerialListEditor from '../common/ImeiSerialListEditor';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import type { ImeiMacRow } from '@/utils/imeiExcel';

interface AddPurchaseInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: string;
}

type PurchaseLine = {
  item: string;
  item_name: string;
  variant: string | null;
  quantity: number | string;
  rate: number | string;
  amount: number;
  allow_fraction: number;
  has_variants: number;
  available_variants: Array<{ name: string; variant_name: string }>;
  track_serials: number;
  track_batches: number;
  batch_no: string;
  expiry_date: string;
  serials: ImeiMacRow[];
};

export default function AddPurchaseInvoiceModal({ isOpen, onClose, supplier }: AddPurchaseInvoiceModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const defaultItem = (): PurchaseLine => ({
    item: '',
    item_name: '',
    variant: null,
    quantity: 1,
    rate: 0,
    amount: 0,
    allow_fraction: 0,
    has_variants: 0,
    available_variants: [],
    track_serials: 0,
    track_batches: 0,
    batch_no: '',
    expiry_date: '',
    serials: [],
  });

  const defaultPayment = {
    date: new Date().toISOString().slice(0, 10),
    payment_method: 'Cash',
    bank_account: '',
    amount: '',
    reference_no: '',
  };

  const [items, setItems] = useState<PurchaseLine[]>([defaultItem()]);
  const [payments, setPayments] = useState<any[]>([{ ...defaultPayment }]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedSupplier, setSelectedSupplier] = useState(supplier);
  const [createStockEntry, setCreateStockEntry] = useState(true);
  const [notes, setNotes] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [adjustAdvanceAmount, setAdjustAdvanceAmount] = useState<number | ''>('');

  const { data: supplierAdvance } = useQuery({
    queryKey: ['supplierAdvance', selectedSupplier],
    queryFn: async () => {
      if (!selectedSupplier) return 0;
      const res = await posApi.getSupplierAdvance(selectedSupplier);
      return res.data.message?.unallocated_advance || 0;
    },
    enabled: !!selectedSupplier
  });

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const addRow = () => {
    setItems([...items, defaultItem()]);
  };

  const syncSerialRowsForQty = (qtyRaw: number | string, existing: ImeiMacRow[]): ImeiMacRow[] => {
    const qty = Math.max(0, Math.floor(Number(qtyRaw) || 0));
    const next = [...existing];
    while (next.length < qty) next.push({ imei: '', mac_address: '' });
    return next.slice(0, qty);
  };

  const updateItemRow = (index: number, selected: any | null) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (!selected) {
        return { ...defaultItem(), quantity: item.quantity };
      }
      const rate = selected.cost_price || selected.price || 0;
      const trackSerials = selected.track_serials ? 1 : 0;
      const trackBatches = selected.track_batches ? 1 : 0;
      const qty = trackSerials ? Math.max(1, Math.floor(Number(item.quantity) || 1)) : item.quantity;
      return {
        ...item,
        item: selected.name,
        item_name: selected.item_name,
        variant: null,
        rate,
        quantity: qty,
        amount: Number(qty) * rate,
        allow_fraction: trackSerials ? 0 : (selected.allow_fractional_qty ? 1 : 0),
        has_variants: selected.has_variants ? 1 : 0,
        available_variants: selected.variants || [],
        track_serials: trackSerials,
        track_batches: trackBatches,
        batch_no: '',
        expiry_date: '',
        serials: trackSerials ? syncSerialRowsForQty(qty, []) : [],
      };
    }));
  };

  const updateItem = (index: number, field: string, value: any) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const updated: PurchaseLine = { ...item, [field]: value };
      if (field === 'quantity') {
        if (item.track_serials) {
          updated.quantity = Math.max(0, Math.floor(Number(value) || 0));
          updated.serials = syncSerialRowsForQty(updated.quantity, item.serials);
        }
        updated.amount = (Number(updated.quantity) || 0) * (Number(updated.rate) || 0);
      } else if (field === 'rate') {
        updated.amount = (Number(updated.quantity) || 0) * (Number(updated.rate) || 0);
      }
      return updated;
    }));
  };

  const updateSerials = (index: number, serials: ImeiMacRow[], quantity?: number) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const qty = quantity != null ? quantity : Number(item.quantity) || 0;
        const rate = Number(item.rate) || 0;
        return {
          ...item,
          serials,
          quantity: quantity != null ? quantity : item.quantity,
          amount: qty * rate,
        };
      }),
    );
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const addPayment = () => {
    setPayments([...payments, { ...defaultPayment, date: new Date().toISOString().slice(0, 10) }]);
  };

  const updatePayment = (index: number, field: string, value: any) => {
    setPayments(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const updated = { ...p, [field]: value };
      if (field === 'payment_method') {
        if (value === 'Bank' && !updated.bank_account && banks.length) {
          const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
          updated.bank_account = def.name;
        }
        if (value !== 'Bank') updated.bank_account = '';
      }
      return updated;
    }));
  };

  const removePayment = (index: number) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const itemTotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const manualPaidAmount = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const advancePaid = Number(adjustAdvanceAmount) || 0;
  const paidAmount = manualPaidAmount + advancePaid;
  const balance = itemTotal - paidAmount;

  useEffect(() => {
    const advanceAvailable = supplierAdvance || 0;
    const advanceToUse = Math.min(advanceAvailable, itemTotal);
    setAdjustAdvanceAmount(advanceToUse > 0 ? advanceToUse : '');

    setPayments(prev => {
      if (prev.length === 0) return prev;
      const remaining = Math.max(0, itemTotal - advanceToUse);
      const updated = [...prev];
      updated[0] = { ...updated[0], amount: remaining > 0 ? remaining : '' };
      return updated;
    });
  }, [itemTotal, supplierAdvance]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (balance < 0) {
        throw new Error("Total payment amount cannot exceed the invoice total");
      }
      if (!selectedSupplier) throw new Error("Supplier is required");
      if (items.length === 0) throw new Error("At least one item is required");
      for (const item of items) {
        if (!item.item) throw new Error("Please select an item for all rows");
        if (item.has_variants && !item.variant) throw new Error(`Please select a variant for ${item.item_name}`);
        const qty = Number(item.quantity) || 0;
        if (!item.allow_fraction && !Number.isInteger(qty)) {
          throw new Error(`Fractions not allowed for item ${item.item_name}`);
        }
        if (createStockEntry && item.track_batches) {
          if (!item.batch_no?.trim()) throw new Error(`Batch number required for ${item.item_name}`);
          if (!item.expiry_date) throw new Error(`Expiry date required for ${item.item_name}`);
        }
        if (createStockEntry && item.track_serials) {
          if (!Number.isInteger(qty) || qty <= 0) {
            throw new Error(`Serial item ${item.item_name} needs a positive whole quantity`);
          }
          if (item.serials.length !== qty) {
            throw new Error(`${item.item_name}: enter exactly ${qty} IMEI(s)`);
          }
          const seen = new Set<string>();
          for (const row of item.serials) {
            const imei = row.imei.trim();
            if (!imei) throw new Error(`Each IMEI is required for ${item.item_name}`);
            if (seen.has(imei)) throw new Error(`Duplicate IMEI: ${imei}`);
            seen.add(imei);
          }
        }
      }

      const paymentRows = payments
        .filter(p => Number(p.amount) > 0)
        .map((p) => {
          if (p.payment_method === 'Bank' && !p.bank_account) {
            throw new Error('Select a bank for bank payments');
          }
          const bank = banks.find((b: { name: string; bank_name: string }) => b.name === p.bank_account);
          return {
            ...p,
            bank_account: p.payment_method === 'Bank' ? p.bank_account : '',
            bank_name: p.payment_method === 'Bank' ? (bank?.bank_name || '') : '',
            account: p.payment_method === 'Bank' ? (bank?.bank_name || '') : '',
          };
        });

      let attachment = '';
      if (attachmentFile) {
        const fd = new FormData();
        fd.append('file', attachmentFile);
        const uploadRes = await saasApi.uploadFile(fd);
        attachment = uploadRes.data.message?.file_url || '';
      }

      const payloadItems = items.map((item) => ({
        item: item.item,
        item_name: item.item_name,
        variant: item.variant,
        quantity: Number(item.quantity) || 0,
        rate: Number(item.rate) || 0,
        amount: Number(item.amount) || 0,
        track_serials: item.track_serials,
        track_batches: item.track_batches,
        batch_no: item.track_batches ? item.batch_no.trim() : undefined,
        expiry_date: item.track_batches ? item.expiry_date : undefined,
        serials: item.track_serials
          ? item.serials.map((s) => ({
              imei: s.imei.trim(),
              mac_address: s.mac_address.trim() || undefined,
            }))
          : undefined,
      }));

      const payload = {
        supplier: selectedSupplier,
        invoice_date: date,
        create_stock_entry: createStockEntry ? 1 : 0,
        status: balance <= 0 ? 'Paid' : (paidAmount > 0 ? 'Partially Paid' : 'Unpaid'),
        item_total: itemTotal,
        net_total: itemTotal,
        notes,
        attachment: attachment || undefined,
        items: payloadItems,
        payments: paymentRows,
        adjust_advance: advancePaid
      };

      await createPurchaseRouted(payload);
    },
    onSuccess: () => {
      toast.success('Purchase Invoice created successfully');
      queryClient.invalidateQueries({ queryKey: ['purchaseInvoices', selectedSupplier] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setItems([defaultItem()]);
      setPayments([{ ...defaultPayment, date: new Date().toISOString().slice(0, 10) }]);
      setNotes('');
      setAttachmentFile(null);
      setAdjustAdvanceAmount('');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to create invoice');
    }
  });

  if (!isOpen) return null;

  const renderItemCard = (item: PurchaseLine, index: number) => (
    <div key={index} className="border border-gray-200 rounded-xl p-3 sm:p-4 bg-white space-y-3 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.6fr)_5.5rem_6.5rem_auto] gap-2 items-end">
            <div className="min-w-0">
              <label className="block text-xs font-medium text-gray-500 mb-1">Item</label>
              <ItemServiceSelect
                type="Item"
                value={item.item || ''}
                label={item.item_name || ''}
                onChange={(id) => { if (!id) updateItemRow(index, null); }}
                onItemSelect={(selected) => { if (selected) updateItemRow(index, selected); }}
                parentItemsOnly
                hideAllOption
                placeholder="Search items..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
              <input
                type="number"
                step={item.allow_fraction ? 'any' : '1'}
                value={item.quantity}
                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Rate ({getCurrencySymbol()})</label>
              <input
                type="number"
                value={item.rate}
                onChange={(e) => updateItem(index, 'rate', e.target.value)}
                className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end justify-between sm:justify-start gap-2 pb-0.5 min-w-[7rem]">
              <div>
                <span className="block text-xs font-medium text-gray-500 mb-1">Amount</span>
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {formatMoney(Number(item.amount || 0))}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 shrink-0"
                aria-label="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {item.has_variants ? (
            <select
              value={item.variant || ''}
              onChange={(e) => updateItem(index, 'variant', e.target.value)}
              className="w-full sm:max-w-xs px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Select Variant...</option>
              {item.available_variants?.map((v) => (
                <option key={v.name} value={v.name}>{v.variant_name}</option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      {!!item.track_batches && createStockEntry && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Batch No <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
              value={item.batch_no}
              onChange={(e) => updateItem(index, 'batch_no', e.target.value)}
              placeholder="BATCH-001"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Expiry Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              value={item.expiry_date}
              onChange={(e) => updateItem(index, 'expiry_date', e.target.value)}
            />
          </div>
        </div>
      )}

      {!!item.track_serials && createStockEntry && (
        <ImeiSerialListEditor
          serials={item.serials}
          quantity={Math.floor(Number(item.quantity) || 0)}
          note="Serial-tracked items require an online connection."
          onChange={(serials, qty) => updateSerials(index, serials, qty)}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900">{t('purchaseInvoice.title')}</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier <span className="text-red-500">*</span>
              </label>
              <SupplierSelect
                required
                value={selectedSupplier}
                onChange={setSelectedSupplier}
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
              <div className="relative">
                <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          <div className="hidden">
            <input
              type="checkbox"
              id="createStockEntry"
              checked={createStockEntry}
              onChange={(e) => setCreateStockEntry(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="createStockEntry" className="ml-2 block text-sm text-gray-900 font-medium">
              Create Stock Entry (Stock In)
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">{t('purchaseInvoice.items')}</h4>
            </div>
            <div className="space-y-3">
              {items.map((item, index) => renderItemCard(item, index))}
              {items.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-8 border border-dashed border-gray-200 rounded-xl">
                  No items added yet.
                </p>
              )}
            </div>
            <div className="mt-3">
              <button type="button" onClick={addRow} className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium">
                <Plus className="w-4 h-4" /> {t('purchaseInvoice.addRow')}
              </button>
            </div>
          </div>

          {(supplierAdvance || 0) > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h4 className="font-medium text-blue-900">Supplier Advance Available</h4>
                <p className="text-sm text-blue-700">{formatMoney((supplierAdvance || 0))}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="text-sm font-medium text-blue-900">Adjust Amount ({getCurrencySymbol()}):</label>
                <input
                  type="number"
                  min="0"
                  max={supplierAdvance || 0}
                  value={adjustAdvanceAmount}
                  onChange={e => {
                    const val = Number(e.target.value);
                    if (val > (supplierAdvance || 0)) setAdjustAdvanceAmount(supplierAdvance || 0);
                    else setAdjustAdvanceAmount(e.target.value === '' ? '' : val);
                  }}
                  className="w-full sm:w-28 px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">{t('purchaseInvoice.payments')}</h4>
              <button type="button" onClick={addPayment} className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                <Plus className="w-4 h-4" /> {t('purchaseInvoice.addPayment')}
              </button>
            </div>
            <div className="space-y-3">
              {payments.map((p, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end border border-gray-100 rounded-lg p-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Date</label>
                    <input
                      type="date"
                      value={p.date}
                      onChange={e => updatePayment(index, 'date', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-200 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Method</label>
                    <select
                      value={p.payment_method}
                      onChange={e => updatePayment(index, 'payment_method', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-200 rounded text-sm"
                    >
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank</option>
                    </select>
                  </div>
                  {p.payment_method === 'Bank' && (
                    <div className="sm:col-span-3">
                      <label className="block text-xs text-gray-500 mb-1">Bank</label>
                      <BankAccountSelect
                        value={p.bank_account}
                        onChange={(v) => updatePayment(index, 'bank_account', v)}
                        banks={banks}
                      />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Amount</label>
                    <input
                      type="number"
                      value={p.amount}
                      onChange={e => updatePayment(index, 'amount', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-200 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Reference</label>
                    <input
                      type="text"
                      value={p.reference_no}
                      onChange={e => updatePayment(index, 'reference_no', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-200 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    {payments.length > 1 && (
                      <button type="button" onClick={() => removePayment(index)} className="p-2 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
            <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
              <Paperclip className="w-4 h-4" />
              {attachmentFile ? attachmentFile.name : t('purchaseInvoice.attachFile')}
              <input
                type="file"
                className="hidden"
                onChange={e => setAttachmentFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-700 space-y-0.5">
            <div>Total: <span className="font-semibold">{formatMoney(itemTotal)}</span></div>
            <div>Paid: <span className="font-semibold">{formatMoney(paidAmount)}</span></div>
            <div>Balance: <span className="font-semibold">{formatMoney(balance)}</span></div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">{t('common.cancel')}</button>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="btn-primary"
            >
              {saveMutation.isPending ? t('common.saving') : t('purchaseInvoice.saveInvoice')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
